import { Link } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import SEOPageLayout from "@/components/SEOPageLayout";

const HERO_IMG =
  "https://images.unsplash.com/photo-1585071258252-369a36d89e30?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export default function CustomBasketballJerseysNZ() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Custom Basketball Jerseys NZ",
    serviceType: "Custom basketball jersey design and manufacturing",
    provider: {
      "@type": "LocalBusiness",
      name: "Tribal Designz",
      address: { "@type": "PostalAddress", addressLocality: "Auckland", addressCountry: "NZ" },
      telephone: "+64 22 101 2443",
    },
    areaServed: { "@type": "Country", name: "New Zealand" },
  };

  return (
    <>
      <SEOHead
        title="Custom Basketball Jerseys NZ | Auckland Studio"
        description="Custom basketball jerseys & shorts designed in Auckland and shipped NZ-wide. Sublimated, school colours, club logos, cultural patterns. Free design concept in 24 hours."
        path="/custom-basketball-jerseys-nz"
        image={HERO_IMG}
        jsonLd={jsonLd}
      />

      <SEOPageLayout
        heroEyebrow="Hoops · Aotearoa NZ"
        heroH1="Custom Basketball Jerseys NZ — Look Pro On Court"
        heroSub="Sublimated custom basketball jerseys and shorts for Kiwi clubs, schools and rep teams. Designed in Auckland, shipped across New Zealand."
        heroImage={HERO_IMG}
        heroImageAlt="Custom basketball jersey design — Auckland NZ studio"
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Custom Basketball Jerseys NZ" },
        ]}
      >
        <h2>The fastest-growing sport in Aotearoa deserves better kit</h2>
        <p>
          Basketball in New Zealand is having a moment. From the Sal's NBL to
          school senior A leagues, from rep tournaments to local Saturday
          competitions, more Kiwis than ever are picking up the orange ball.
          And yet most local teams are still pulling on plain reversibles or
          mass-produced templates that look identical to every other side in
          the comp. <strong>Custom basketball jerseys</strong> are how your
          team starts looking — and feeling — like the team it wants to be.
        </p>
        <p>
          Tribal Designz designs and manufactures fully sublimated
          basketball uniforms for clubs, schools and rep programmes across
          New Zealand. Every kit is custom from the ground up: your colours,
          your wordmark, your pattern, your numbers, your sponsor placements.
          We don't do reversible-and-call-it-custom. We do designed-from-scratch.
        </p>

        <h2>What's in a Tribal Designz basketball kit</h2>
        <ul>
          <li>Custom <strong>basketball jerseys</strong> — full sublimation, mesh or interlock</li>
          <li>Matching basketball shorts with side-piping &amp; pocket options</li>
          <li>Reversibles (light/dark) for training squads</li>
          <li>Warm-up tees, shooting shirts &amp; hoodies</li>
          <li>Coach polos &amp; manager apparel</li>
          <li>Custom number sets, player names, captain's "C", patches</li>
        </ul>

        <h2>Sublimation = colour-accurate, won't crack</h2>
        <p>
          We sublimate every basketball jersey because it's the only print
          method that lets you do photo-quality patterns, gradients and
          large-scale designs without the heavy, cracking feel of vinyl
          transfers. The dye becomes part of the fabric — it can't peel,
          flake or fade. Player names and numbers can be sublimated in too,
          so there's nothing stuck to the fabric to come off in the wash.
        </p>

        <h2>Cultural design done right</h2>
        <p>
          Many of our basketball clients are Pasifika or Māori clubs who
          want their cultural identity reflected in their kit — tatau-style
          line work down the side panel, siapo-inspired patterns on the
          shorts, te reo wordmarks on the chest. We've designed kits with
          tāniko bands, kowhaiwhai motifs and Polynesian geometric work
          tastefully integrated into modern basketball silhouettes. We do
          this respectfully, in collaboration with you, with cultural
          appropriateness as part of every brief.
        </p>

        <h2>School senior A &amp; rep team kits</h2>
        <p>
          We outfit a lot of secondary school basketball programmes — senior
          A and senior B teams, junior development squads, and tournament
          travel kits. School orders typically include the playing jersey
          set, training reversibles, warm-up tee and travel hoodies. We work
          with your school's branding guidelines and integrate sponsor logos
          where required.
        </p>

        <h2>Lead times &amp; minimums</h2>
        <p>
          Minimum order is <strong>10 units</strong> and standard turnaround
          is <strong>2–3 weeks</strong> from artwork approval. Heading into a
          tournament with short notice? Talk to us about expedited
          production or our{" "}
          <Link to="/fast-tshirt-printing-auckland">Fast Print (DTF) service</Link>{" "}
          for warm-up tees and supporter merch.
        </p>

        <h2>Auckland based · NZ-wide delivery</h2>
        <p>
          We're based in Auckland and deliver across New Zealand. Whether
          you're in Manukau or Mosgiel, your custom basketball kit ships to
          your door (or to your tournament hotel if it's a rush). Auckland
          teams can also collect directly from the studio.
        </p>

        <h2>More from Tribal Designz</h2>
        <p>
          Need <Link to="/custom-rugby-uniforms-nz">custom rugby uniforms</Link>{" "}
          too? We do that. Looking at{" "}
          <Link to="/custom-uniforms-auckland">team uniforms across Auckland</Link>?{" "}
          See the full picture. Or check our blog on{" "}
          <Link to="/blog/sublimation-vs-dtf-printing">sublimation vs DTF printing</Link>{" "}
          to understand which method is right for your kit.
        </p>
      </SEOPageLayout>
    </>
  );
}
